import { useState, useEffect, useCallback, useRef } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useUserGeneratedContent } from '../../../../models/user-generated-contents/context';
import useMediaUploader from '../../../../common/use-media-uploader';
import type {
  ContentImage,
  MediaGroup,
} from '../../../../models/user-generated-contents/types';

export const MEDIA_GROUP_TYPE = 'images' as const;
export const MAX_IMAGES = 10;
export const MAX_IMAGE_SIZE_MB = 10;
export const MAX_IMAGE_SIZE = MAX_IMAGE_SIZE_MB * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES = 'image/jpeg, image/png, image/gif';

// `id` identifies the kind of error so it can be individually dismissed and so the
// counter's error state can be derived from it (see LIMIT_ERROR_ID).
export type UploaderError = { id?: string; title: string; description: string };

// The error id that puts the counter into its error state.
export const LIMIT_ERROR_ID = 'too-many-files';

export type UseImageUploaderReturn = {
  images: ContentImage[];
  inProgress: File[];
  totalCount: number;
  isAtLimit: boolean;
  isUploading: boolean;
  errors: UploaderError[];
  // True while an over-limit error is being shown; drives the counter's error state.
  limitError: boolean;
  clearErrors: () => void;
  // Dismiss a single error by its position in `errors` (the alert's close button).
  dismissError: (index: number) => void;
  getInputProps: ReturnType<typeof useDropzone>['getInputProps'];
  open: ReturnType<typeof useDropzone>['open'];
  handleDropAccepted: (files: File[]) => void;
  handleDropRejected: (rejections: FileRejection[]) => void;
  handleDelete: (index: number) => void;
  handleReorder: (images: ContentImage[]) => void;
};

const useImageUploader = (): UseImageUploaderReturn => {
  const { t } = useTranslation();
  const {
    userGeneratedContent,
    updateAttachmentGroup,
    updateUserGeneratedContentFormState,
  } = useUserGeneratedContent();

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<UploaderError[]>([]);
  const limitError = errors.some((error) => error.id === LIMIT_ERROR_ID);

  const attachments = userGeneratedContent.attachments ?? [];
  const imagesGroup = attachments.find(
    (g): g is Extract<MediaGroup, { type: typeof MEDIA_GROUP_TYPE }> =>
      g.type === MEDIA_GROUP_TYPE
  );
  const images: ContentImage[] = imagesGroup?.items ?? [];

  // For the cover image: keep the latest cover URL and images in refs so async
  // callbacks always read current values (update refs synchronously on render).
  const coverImageUrl = userGeneratedContent.content_cover?.image?.url;
  const coverImageUrlRef = useRef<string | undefined>(coverImageUrl);
  coverImageUrlRef.current = coverImageUrl;

  const imagesRef = useRef<ContentImage[]>(images);
  imagesRef.current = images;

  const totalCount =
    images.length + (currentFile ? 1 : 0) + pendingFiles.length;
  const isAtLimit = totalCount >= MAX_IMAGES;

  const { status } = useMediaUploader({
    file: currentFile,
    onStatusChange: undefined,
    onComplete: (url: string) => {
      updateAttachmentGroup(MEDIA_GROUP_TYPE, (prev) => [
        ...prev,
        { url, order: prev.length },
      ]);
      // FE-34409: default the cover image to the first uploaded post image
      // when the creator hasn't chosen one yet.
      if (!coverImageUrlRef.current) {
        coverImageUrlRef.current = url;
        updateUserGeneratedContentFormState({
          content_cover: { image: { url } },
        });
      }
      setCurrentFile(null);
    },
    onError: () => {
      setErrors([
        {
          id: 'upload',
          title: '',
          description: t('user_generated_content.media.images.error_upload'),
        },
      ]);
      setCurrentFile(null);
    },
  });

  const isUploading = status === 'fetching' || status === 'uploading';

  useEffect(() => {
    if (!currentFile && pendingFiles.length > 0) {
      const [next, ...rest] = pendingFiles;
      setCurrentFile(next);
      setPendingFiles(rest);
    }
  }, [currentFile, pendingFiles]);

  const handleDropAccepted = useCallback(
    (files: File[]) => {
      const available = MAX_IMAGES - totalCount;
      if (files.length > available) {
        setErrors([
          {
            id: LIMIT_ERROR_ID,
            title: '',
            description: t(
              'user_generated_content.media.images.error_too_many_files',
              { max: MAX_IMAGES }
            ),
          },
        ]);
      } else {
        setErrors([]);
      }
      setPendingFiles((prev) => [...prev, ...files.slice(0, available)]);
    },
    [t, totalCount]
  );

  const handleDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      const errors = new Map<string, UploaderError>();
      for (const rejection of rejections) {
        const code = rejection?.errors[0]?.code;
        if (code === 'too-many-files') {
          errors.set('too-many-files', {
            id: LIMIT_ERROR_ID,
            title: '',
            description: t(
              'user_generated_content.media.images.error_too_many_files',
              { max: MAX_IMAGES }
            ),
          });
        } else if (code === 'file-too-large') {
          errors.set('file-too-large', {
            id: 'file-too-large',
            title: t('user_generated_content.media.error_too_large_title'),
            description: t('user_generated_content.media.error_too_large', {
              maxSize: MAX_IMAGE_SIZE_MB,
            }),
          });
        } else {
          errors.set('rejected', {
            id: 'rejected',
            title: t('common.error_rejected_title'),
            description: t(
              'user_generated_content.media.images.error_rejected',
              { maxSize: MAX_IMAGE_SIZE_MB }
            ),
          });
        }
      }
      setErrors(Array.from(errors.values()));
    },
    [t]
  );

  const { getInputProps, open } = useDropzone({
    accept: ACCEPTED_IMAGE_TYPES,
    maxSize: MAX_IMAGE_SIZE,
    maxFiles: MAX_IMAGES,
    multiple: true,
    noClick: true,
    noDrag: true,
    disabled: isAtLimit,
    onDropAccepted: handleDropAccepted,
    onDropRejected: handleDropRejected,
  });

  const handleDelete = useCallback(
    (index: number) => {
      const deletedUrl = imagesRef.current[index]?.url;
      updateAttachmentGroup(MEDIA_GROUP_TYPE, (prev) => {
        const next = prev
          .filter((_, i) => i !== index)
          .map((img, i) => ({ ...img, order: i }));
        return next.length > 0 ? next : undefined;
      });
      // FE-34409: if the deleted image is the one that was copied to the cover,
      // clear the cover too.
      if (deletedUrl && deletedUrl === coverImageUrlRef.current) {
        coverImageUrlRef.current = undefined;
        updateUserGeneratedContentFormState({ content_cover: undefined });
      }
      setErrors([]);
    },
    [updateAttachmentGroup, updateUserGeneratedContentFormState]
  );

  const handleReorder = useCallback(
    (reordered: ContentImage[]) => {
      updateAttachmentGroup(MEDIA_GROUP_TYPE, () => reordered);
      setErrors([]);
    },
    [updateAttachmentGroup]
  );

  const clearErrors = useCallback(() => setErrors([]), []);

  const dismissError = useCallback((index: number) => {
    setErrors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const inProgress = currentFile
    ? [currentFile, ...pendingFiles]
    : pendingFiles;

  return {
    images,
    inProgress,
    totalCount,
    isAtLimit,
    isUploading,
    errors,
    limitError,
    clearErrors,
    dismissError,
    getInputProps,
    open,
    handleDropAccepted,
    handleDropRejected,
    handleDelete,
    handleReorder,
  };
};

export default useImageUploader;
